import React from "react";
import { isScaleLikeType, scaleBounds } from "./formFieldHelpers";

/**
 * Editable renderer for a single form field. Produces the value shape the
 * backend expects in responses[field.id]:
 *   short_text/long_text -> string, checkbox -> bool, yes_no -> bool (No is a
 *   real answer), single_select -> chosen option label (string),
 *   multi_select -> array of option labels, scale -> integer.
 *
 * Shared by the in-app form (Form.js, complete mode) and the public form page
 * (PublicFormView) so the fill UI can't drift. `onChange` receives the new
 * value; headings/unknown types render nothing.
 */
export function FormFieldInput({ field: f, value: v, onChange }) {
  if (f.type === "short_text") {
    return (
      <input
        type="text"
        value={v || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
      />
    );
  }
  if (f.type === "long_text") {
    return (
      <textarea
        rows={3}
        value={v || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700 resize-none"
      />
    );
  }
  if (f.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={!!v}
          onChange={(e) => onChange(e.target.checked)}
          className="accent-violet-600 w-4 h-4 shrink-0"
        />
        <span className="whitespace-pre-wrap">{f.help || "Check to confirm"}</span>
      </label>
    );
  }
  if (f.type === "yes_no") {
    return (
      <div className="flex flex-wrap gap-2">
        {[
          { label: "Yes", value: true },
          { label: "No", value: false },
        ].map((opt) => {
          const selected = v === opt.value;
          return (
            <button
              key={opt.label}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`px-6 py-2 rounded-lg text-sm border transition-colors sm:flex-1 ${
                selected
                  ? "bg-violet-700 border-violet-700 text-white"
                  : "bg-white border-gray-300 text-slate-700 hover:border-violet-400"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  }
  if (f.type === "multi_select") {
    const chosen = Array.isArray(v) ? v : [];
    const toggle = (label) =>
      onChange(
        chosen.includes(label) ? chosen.filter((c) => c !== label) : [...chosen, label]
      );
    return (
      <div className="flex flex-wrap gap-2">
        {(f.options || []).map((opt, i) => {
          const selected = chosen.includes(opt.label);
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggle(opt.label)}
              aria-pressed={selected}
              // The box is a flex child, not an inline-block nudged with
              // vertical-align: aligning it to the text baseline left it
              // sitting low against the label, and lower still once a long
              // option wrapped to a second line.
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border text-left whitespace-normal break-words transition-colors sm:w-full ${
                selected
                  ? "bg-violet-700 border-violet-700 text-white"
                  : "bg-white border-gray-300 text-slate-700 hover:border-violet-400"
              }`}
            >
              <span
                className={`flex w-3.5 h-3.5 shrink-0 items-center justify-center rounded border ${
                  selected ? "bg-white border-white" : "border-gray-400 bg-white"
                }`}
                aria-hidden
              >
                {selected && (
                  <span className="text-violet-700 text-[10px] leading-none">✓</span>
                )}
              </span>
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    );
  }
  if (f.type === "single_select") {
    return (
      <div className="flex flex-wrap gap-2">
        {(f.options || []).map((opt, i) => {
          const selected = v === opt.label;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onChange(opt.label)}
              // text-left + wrapping so a long option label never overflows the
              // viewport on a phone; sm:w-full (mobile, reversed breakpoints)
              // stacks options full-width for easier tapping. Inline pills on
              // desktop.
              className={`px-4 py-2 rounded-lg text-sm border text-left whitespace-normal break-words transition-colors sm:w-full ${
                selected
                  ? "bg-violet-700 border-violet-700 text-white"
                  : "bg-white border-gray-300 text-slate-700 hover:border-violet-400"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  }
  if (isScaleLikeType(f.type)) {
    const { min, max } = scaleBounds(f);
    const buttons = [];
    for (let n = min; n <= max; n++) buttons.push(n);
    const hasLabels = !!(f.min_label || f.max_label);
    return (
      <div>
        {/* The steps stretch to fill the row so the end labels below line up
            with the lowest and highest step (justify-between spans the same
            width). A 2.5rem floor keeps them tappable, wrapping instead of
            shrinking when a long scale meets a narrow screen. */}
        <div className="flex flex-wrap gap-2">
          {buttons.map((n) => {
            const selected = v !== null && v !== undefined && v !== "" && Number(v) === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onChange(n)}
                className={`h-10 flex-1 basis-10 min-w-[2.5rem] rounded-lg text-sm border transition-colors ${
                  selected
                    ? "bg-violet-700 border-violet-700 text-white"
                    : "bg-white border-gray-300 text-slate-700 hover:border-violet-400"
                }`}
              >
                {n}
              </button>
            );
          })}
        </div>
        {hasLabels && (
          <div className="mt-1.5 flex justify-between text-xs text-slate-500">
            <span>{f.min_label || ""}</span>
            <span>{f.max_label || ""}</span>
          </div>
        )}
      </div>
    );
  }
  return null;
}
